import { clone, UnitSystems, UnitSystemType } from '@fulcrum/lib';
import {
  FeetInchUnit,
  FormattingSettings,
  FormattingSettingsValidationResult,
  isFormattingSettings,
} from '@trimble/types';
import { getFormattingSystemDefaults } from './formatting-settings-defaults';

export function validateFormattingSettings(
  settings: FormattingSettings,
  projectInMasterRegion: boolean
): FormattingSettingsValidationResult {
  let unitSystemType: UnitSystemType = settings.unitSystem
    .toString()
    .toLocaleLowerCase() as UnitSystemType;
  let unitSystem = UnitSystems[unitSystemType];
  let isInvalidUnitSystem: boolean = false;
  let hasInvalidUnits: boolean = false;

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!unitSystem) {
    isInvalidUnitSystem = true;
    unitSystemType = projectInMasterRegion ? 'imperial' : 'metric';
    unitSystem = UnitSystems[unitSystemType];
  }

  delete unitSystem.Area.km2;
  delete unitSystem.Volume.km3;

  const defaultUnitSystemType =
    unitSystemType === 'custom' ? 'metric' : unitSystemType;
  const defaults = getFormattingSystemDefaults(defaultUnitSystemType);

  if (isInvalidUnitSystem) {
    // If there was an unknown/unsupported unit system defined, fallback to the region specific default setting.
    const formattingSettings = isFormattingSettings(settings)
      ? settings
      : undefined;
    return { settings: formattingSettings, isInvalidUnitSystem };
  } else {
    // In case a matching unit system has been found, start validating the used units too...
    let fallbackUnitDefaults: Partial<FormattingSettings> = {
      unitSystem: unitSystemType,
    };
    const fullLengthUnits = [...Object.keys(unitSystem.Length), FeetInchUnit];

    if (!fullLengthUnits.includes(settings.lengthUnit)) {
      // Fallback to unit system specific default 'length' settings.
      fallbackUnitDefaults = clone(fallbackUnitDefaults, {
        lengthUnit: defaults.lengthUnit,
        lengthFormatting: defaults.lengthFormatting,
        lengthDecimals: defaults.lengthDecimals,
        lengthFractions: defaults.lengthFractions,
      });
    }
    if (!fullLengthUnits.includes(settings.lengthMeasurementUnit)) {
      // Fallback to unit system specific default 'length measurement' settings.
      fallbackUnitDefaults = clone(fallbackUnitDefaults, {
        lengthMeasurementUnit: defaults.lengthMeasurementUnit,
        lengthMeasurementFormatting: defaults.lengthMeasurementFormatting,
        lengthMeasurementDecimals: defaults.lengthMeasurementDecimals,
        lengthMeasurementFractions: defaults.lengthMeasurementFractions,
      });
    }
    if (!Object.keys(unitSystem.Area).includes(settings.areaUnit ?? '')) {
      // Fallback to unit system specific default 'area' settings.
      fallbackUnitDefaults = clone(fallbackUnitDefaults, {
        areaUnit: defaults.areaUnit,
        areaDecimals: defaults.areaDecimals,
      });
    }
    if (!Object.keys(unitSystem.Volume).includes(settings.volumeUnit ?? '')) {
      // Fallback to unit system specific default 'volume' settings.
      fallbackUnitDefaults = clone(fallbackUnitDefaults, {
        volumeUnit: defaults.volumeUnit,
        volumeDecimals: defaults.volumeDecimals,
      });
    }
    if (!Object.keys(unitSystem.Mass).includes(settings.massUnit ?? '')) {
      // Fallback to unit system specific default 'mass' settings.
      fallbackUnitDefaults = clone(fallbackUnitDefaults, {
        massUnit: defaults.massUnit,
        massDecimals: defaults.massDecimals,
      });
    }
    if (!Object.keys(unitSystem.Angle).includes(settings.angleUnit ?? '')) {
      // Fallback to unit system specific default 'angle' settings.
      fallbackUnitDefaults = clone(fallbackUnitDefaults, {
        angleUnit: defaults.angleUnit,
        angleDecimals: defaults.angleDecimals,
      });
    }

    if (Object.keys(fallbackUnitDefaults).length > 1) {
      hasInvalidUnits = true;
      settings = clone(settings, fallbackUnitDefaults);
    }
  }

  return { settings, isInvalidUnitSystem, hasInvalidUnits };
}
